import {useMemo} from 'react';
import {useWorkstreams} from 'views/workstreams/detail/hooks';
import type {AltitudeFilterValue, WorkstreamFilters} from 'views/workstreams/detail/types';
import {
  getDefaultWorkstreamTimeframe,
  narrativePlanningHalfToTimeframeTableFilter,
} from 'views/workstreams/detail/workstreamTimeframe';

/**
 * Profile workstreams card: the ids of a person's workstreams in the current
 * half plus Enablement, which is what /workstreams shows for them by default,
 * from the same list endpoint so the card agrees with its View all page.
 * `person` scope is their own (roles, outcome and Jira owners); `team` scope is
 * their teams'. `ids` is null when there's nothing to filter by (not enabled,
 * or the list failed to load), so the card falls back to every workstream.
 */
export const useCurrentHalfWorkstreamIds = ({
  ldap,
  scope,
  enabled,
}: {
  ldap?: string;
  scope: 'person' | 'team';
  enabled: boolean;
}) => {
  const filters: WorkstreamFilters = useMemo(
    () => ({
      altitude: ['false'] as AltitudeFilterValue[],
      timeframes: [
        narrativePlanningHalfToTimeframeTableFilter(getDefaultWorkstreamTimeframe()),
        'enablement',
      ],
    }),
    []
  );
  const isEnabled = enabled && Boolean(ldap);
  const query = useWorkstreams({
    scope_type: scope,
    scope_value: ldap,
    filters,
    group_by: 'none',
    autoFetchAllPages: true,
    enabled: isEnabled,
  });

  const isLoaded = query.hasFirstPageForCurrentSignature && !query.hasNextPage;
  const rows = query.data?.workstreams;
  const ids = useMemo(
    () => (isEnabled && isLoaded && !query.isError ? new Set(rows?.map(({id}) => id)) : null),
    [isEnabled, isLoaded, query.isError, rows]
  );

  return {ids, isLoading: isEnabled && !isLoaded && !query.isError};
};
